import React from "react"
import {
  CateringCheckoutInput,
  CateringContext,
  CateringPaymentMethod,
} from "./catering-context"
import { Restaurant } from "../../models/restaurant"
import moment from "moment"

type Props = {
  children: React.ReactNode
  restaurant?: Restaurant
}

export type ShopCateringSpec = {
  shopIds: string[]
  menuPageCateringDescription: string
  checkoutCateringDescription: string
  cateringOrderWindowStartInDays: number
  cateringOrderWindowEndInDays: number
  checkoutInputs: CateringCheckoutInput[]
  cateringPaymentMethods: CateringPaymentMethod[]
}

// Use this directly if logic outside of CateringProvider is needed (See triggerPayment in checkout.tsx).
export const getCateringSpec = (shopId: string | undefined) => {
  if (!shopId) {
    return undefined
  }
  return SHOP_CATERING_SPEC.find((spec) => spec.shopIds.includes(shopId))
}

// Madhuset: Catering - Mad & Kærlighed
const madhuset: ShopCateringSpec = {
  cateringOrderWindowStartInDays: 2,
  cateringOrderWindowEndInDays: moment().add(1, "year").diff(moment(), "days"),
  cateringPaymentMethods: ["credit_card"],
  checkoutInputs: [
    {
      name: "allergene",
      label: "Allergener og særlige hensyn",
      placeholder: "Indtast allergener eller madhensyn her",
      helperText:
        "Skriv en note til os her, såfremt der er allergier eller særlige hensyn vi skal tage til bestillingen.",
    },
  ],
  checkoutCateringDescription:
    "Vi anretter charcuteri samt salater på engangs-bambusfade. Sandwichene halveres og sættes i æsker, medmindre andet ønskes.",
  menuPageCateringDescription:
    "Skal du holde fest, reception, firmaarrangement eller noget helt fjerde?\n\nVi laver sandwich, salater og charcuteri anretninger ud af huset.\n\nSe vores udvalg herunder – og hvis I har særlige ønsker eller forespørgsler, er I altid velkomne til at kontakte os på catering@madogkaerlighed.dk",
  shopIds: ["01994d83-f4e3-710a-846d-68c6749536fe"],
}

// Paradis Is
const paradisIs: ShopCateringSpec = {
  cateringOrderWindowStartInDays: 5,
  cateringOrderWindowEndInDays: moment().add(1, "year").diff(moment(), "days"),
  cateringPaymentMethods: ["credit_card"],
  checkoutInputs: [],
  checkoutCateringDescription:
    "Din forespørgsel på dit valg af is til dit event, er nu klar til at blive sendt til os, når du har udfyldt nedenstående oplysninger og har gennemført betalingen. Når vi har modtaget din forespørgsel, hører du fra os indenfor 48 timer. Er din bestilling tilgængelig på din ønskede dato, modtager du en bekræftelse og din bestilling er nu registreret og betalt hos os. Er der fuldt booket på din ønskede dato, kontakter vi dig og aftaler nærmere med dig. Afhentning og levering:\n\nHvad tid kan jeg hente eller få isdisken leveret? Skriv gerne i kommentarfeltet, hvilket tidspunkt der kan passe dig, så aftaler vi nærmere.\n\nHvad tid skal jeg aflevere isdisken? Det skal du dagen efter dit event inden kl.11",
  menuPageCateringDescription:
    "Uanset om det er en fødselsdag, et bryllup, en konfirmation eller et firma arrangement, så vil dine gæster blive begejstret af is fra Paradis. Vores udvalg af isdiske varierer fra butik til butik, men herunder er en oversigt over forskellige isdiske og løsninger i denne butik. Du har mulighed for at få leveret med eller uden bemanding, eller at afhente selv i vores butik. Se mere om de forskellige løsninger vi har, som passer til netop dit event.",
  shopIds: ["01997aa6-f565-7287-acb1-02ad53dd7470"],
}

const slagterBroeBase: Omit<
  ShopCateringSpec,
  "menuPageCateringDescription" | "checkoutCateringDescription" | "shopIds"
> = {
  cateringOrderWindowStartInDays: 1,
  cateringOrderWindowEndInDays: moment().add(1, "year").diff(moment(), "days"),
  cateringPaymentMethods: ["credit_card"],
  checkoutInputs: [],
}

// Slagter Broe Smørrebroed
const slagterBroeSmoerrebroed: ShopCateringSpec = {
  ...slagterBroeBase,
  checkoutCateringDescription:
    "Jeres valgte menu kommer anrettet til deling i engangsemballage. Ønsker I selv at anrette maden på jeres eget serveringsservice, er det let at overføre. I skal selv sørger for tag-tøj til maden.",
  menuPageCateringDescription:
    "Forudbestil smørrebrød til dit næste arrangement eller event direkte fra din lokale slagter. Du kan forudbestille fra dag til dag, og til fremtidige begivenheder.",
  shopIds: ["01990ed3-86f8-735d-9971-95848601bc68"],
}

// Slagter Broe Slagterbutikken
const slagterBroeSlagterbutikken: ShopCateringSpec = {
  ...slagterBroeBase,
  checkoutCateringDescription: "",
  menuPageCateringDescription:
    "Du kan forudbestille fra vores slagterbutik dag til dag, og til fremtidige begivenheder.",
  shopIds: ["01990ecc-729b-7067-8650-980295651cd7"],
}

// Slagter Broe Slagterbutikken 2
const slagterBroeSlagterbutikkenTwo: ShopCateringSpec = {
  ...slagterBroeBase,
  checkoutCateringDescription: "",
  menuPageCateringDescription:
    "Du kan forudbestille fra vores sæsonbutik dag til dag, og til fremtidige begivenheder. Bemærk at butikken opdateres løbende.",
  shopIds: ["01996219-7028-725d-80fa-257f92a5f42d"],
}

const baseMadklubbenConfig: Pick<
  ShopCateringSpec,
  | "cateringOrderWindowStartInDays"
  | "menuPageCateringDescription"
  | "checkoutCateringDescription"
  | "cateringPaymentMethods"
> = {
  cateringOrderWindowStartInDays: 3,
  checkoutCateringDescription:
    "Jeres valgte menu kommer anrettet til deling i engangsemballage. Ønsker I selv at anrette maden på jeres eget serveringsservice, er det let at overføre. I skal selv sørger for tag-tøj til maden.",
  menuPageCateringDescription:
    "Vi tager højde for allergener og særlige madhensyn. Noter eventuelle allergener i kommentarfeltet, når i bestiller. Indpakning: Varme retter bliver pakket i termokasser og kan holde sig lune i op til 2 timer. De kolde retter er pakket i plastik kasser. Antal personer: Minimum 10 personer - Restauranten forbeholder sig retten til at afvise bestillingen, hvis der bestilles til mindre end 10 personer. Maden serveres i engangs-/aluminiumsbakker. Alt maden er anrettet til buffet.",
  cateringPaymentMethods: ["credit_card"],
}

const madklubbenConfigOne: Omit<
  ShopCateringSpec,
  "shopIds" | "cateringOrderWindowEndInDays"
> = {
  ...baseMadklubbenConfig,
  checkoutInputs: [
    {
      name: "catering_pickup_date",
      label: "Returnering af service",
      placeholder: "Skriv dato her",
      helperText:
        "Ved afhentning: Hvilken dato returnere i termokasser (senest 3 dage efter arrangementet). Ved levering: Hvilken dato kan vi afhente termokasser (senest 3 dage efter arrangementet). Vælg et tidsinterval på en time mellem kl. 10.00 og 16.00 alle dage, og skriv dette tidsinterval i kommentarfeltet, så kommer vi og henter termokasserne.",
    },
  ],
}

const madklubbenConfigTwo: Omit<
  ShopCateringSpec,
  "shopIds" | "cateringOrderWindowEndInDays"
> = {
  ...baseMadklubbenConfig,
  checkoutInputs: [
    {
      name: "catering_pickup_date",
      label: "Returnering af service",
      placeholder: "Skriv dato her",
      helperText:
        "Hvilken dato returnerer i termokasser (senest 3 dage efter arrangementet).",
    },
  ],
}

// MK Vesterbro
// MK Østerbro & Home of Carlsberg & Juma
// Food club nørrebro & vesterbro
// Bazaar & Tonys & Roberta
// Hanzo Østerbro & Hanzo Lyngby
const madklubbenConfigOneCustomers: ShopCateringSpec = {
  ...madklubbenConfigOne,
  cateringOrderWindowEndInDays: moment("2030-01-01").diff(moment(), "days"),
  shopIds: [
    "0197b021-48dd-72cb-ab1f-69ae587e01cc",
    "0198cc41-1c3e-72e5-8ded-79d5afa789f4",
    "0198cc3d-d08b-73d7-a9d8-7915c2eea1d7",
    "0199c854-2949-7058-b6fe-8c7cf21ddc53",
    "01986041-cbd0-70e6-8fcc-cee87b07fed0",
    "01986044-415e-7212-853a-a996da8d03fd",
    "01986038-2950-739f-a7cb-bf3d020905f8",
    "0198603b-aa64-72a0-9105-8daa7c134cf5",
    "0198603e-30bf-71b2-bb88-6515a55687ff",
    "0195f11d-bdb3-70a9-8c26-bf46b3c9ef2e",
    "01985fbd-c66c-702f-b71e-9dfea9cfe566",
  ],
}

// Madklubben Lyngby
const madklubbenLyngbyCatering: ShopCateringSpec = {
  ...madklubbenConfigOne,
  cateringOrderWindowEndInDays: moment("2030-01-01").diff(moment(), "days"),
  shopIds: ["019cdc88-d44c-7386-a90a-f5808957b310"],
  cateringPaymentMethods: ["ean"],
}

// Food club Aarhus
// MK Aarhus and MK Odense
// Lupo & MK Esbjerg
// Hanzo Aarhus, Esbjerg & Odense
// MK Lyngby
const madklubbenConfigTwoCustomers: ShopCateringSpec = {
  ...madklubbenConfigTwo,
  cateringOrderWindowEndInDays: moment("2030-01-01").diff(moment(), "days"),
  shopIds: [
    "01986046-f40b-72ad-807d-f5739ea02097",
    "01986031-d8b4-70ba-b670-8d99f1975f7c",
    "0198602c-2aff-7236-871c-454b1bb70966",
    "01986035-908a-70fe-a06f-7b6ebe16ac54",
    "0198602f-1bf9-73a3-9ff7-805256977064",
    "01985fc2-217d-7289-aad3-098cd296cacd",
    "01985fc5-51ad-71aa-b3b2-4491fada1a39",
    "01985fc7-e876-7207-8225-2da72ab47d6e",
    "0197ca76-9339-70a3-8ca6-9c1f3ffe4c92",
    "019c60a4-e409-7008-9c21-a75b37df14c3",
  ],
}

const humblePieThanksgiving: ShopCateringSpec = {
  cateringOrderWindowEndInDays: moment("2026-02-09").diff(moment(), "days"),
  cateringOrderWindowStartInDays: moment("2026-02-09").diff(moment(), "days"),
  cateringPaymentMethods: ["credit_card"],
  checkoutInputs: [],
  shopIds: ["0199b972-f962-70fb-ae20-01509107c75a"],
  checkoutCateringDescription:
    "Bring the true Super Bowl experience home on February 8th with authentic American party food, delivered straight to your door.\n\nFrom juicy burgers and loaded fries to wings and classic sides – we’ve got everything you need for game night.\n\nPerfect for sharing, cheering, and indulging like you’re at a tailgate party in the U.S.\n\nOrder ahead, relax, and let the food be as epic as the game. 🏈🍔",
  menuPageCateringDescription:
    "Bring the true Super Bowl experience home on February 8th with authentic American party food, delivered straight to your door.\n\nFrom juicy burgers and loaded fries to wings and classic sides – we’ve got everything you need for game night.\n\nPerfect for sharing, cheering, and indulging like you’re at a tailgate party in the U.S.\n\nOrder ahead, relax, and let the food be as epic as the game. 🏈🍔",
}

const humblePieCatering: ShopCateringSpec = {
  cateringOrderWindowEndInDays: moment()
    .add(2, "months")
    .diff(moment(), "days"),
  cateringOrderWindowStartInDays: 3,
  cateringPaymentMethods: ["credit_card"],
  checkoutInputs: [],
  shopIds: ["0199b973-8085-7069-88f8-fce80e574ae1"],
  checkoutCateringDescription:
    "Thank you for your order!\nWe’re thrilled that you chose Humble Pie.\nWe’ll review and accept your order as quickly as possible. You’ll receive a notification once our team has confirmed it.",
  menuPageCateringDescription:
    "Welcome to Humble Pie: A Southern Street Food Kitchen!\nWe specialize in Southern fried chicken and classic comfort dishes that are perfect for any event. Whether it’s a wedding, a party, or a cozy family dinner, we bring the taste of the South to your table.\n\nOrder now and let us cater your next gathering with delicious, home-cooked flavors!",
}

const kcal: ShopCateringSpec = {
  cateringOrderWindowEndInDays: moment().add(1, "year").diff(moment(), "days"),
  cateringOrderWindowStartInDays: moment()
    .add(3, "days")
    .diff(moment(), "days"),
  cateringPaymentMethods: ["credit_card"],
  checkoutInputs: [],
  shopIds: [
    "019a0bff-c465-73f0-b5d1-0c3a98ef782c",
    "950dfdd9-eb07-4263-a214-e32a78ba54cf",
  ],
  checkoutCateringDescription:
    "Maden leveres anrettet til deling i praktisk engangsemballage, klar til servering.\nHvis I ønsker at anrette maden på eget service, kan det nemt overføres.\nBemærk, at der ikke medfølger serveringsbestik – dette skal I selv sørge for.",
  menuPageCateringDescription:
    "Hos Kcal tilbyder vi catering ud fra et bredt udvalg af lækre salater, sandwiches og juice, der passer til ethvert arrangement – stort som småt. Uanset om du planlægger et firmaevent, en workshop, en familiesammenkomst eller en stor fest, så vil catering fra Kcal helt sikkert være et hit hos dine gæster.\n\nVil du slippe for alt besvær med madlavning? Vi står klar til at gøre dit arrangement til noget helt særligt med vores smagfulde, grønne menu. Det eneste vi beder om er, at du bestiller minimum 15 produkter. Så klarer vi resten.",
}

const paradis: ShopCateringSpec = {
  cateringOrderWindowEndInDays: moment().add(1, "year").diff(moment(), "days"),
  cateringOrderWindowStartInDays: moment()
    .add(3, "days")
    .diff(moment(), "days"),
  cateringPaymentMethods: ["credit_card"],
  checkoutInputs: [
    {
      helperText:
        "Hvad tid kan jeg hente eller få isdisken leveret og returneret? Skriv afhentning og returneringsdato her",
      label: "Afhentning og levering",
      name: "return_date",
      placeholder:
        "Hvad tid kan jeg hente eller få isdisken leveret og returneret?",
    },
  ],
  shopIds: [
    "019a15a0-b3fa-7368-930a-4aaad6f91991",
    "019a15a5-6858-7104-8a1a-7c66d4b1fef0",
    "019a306f-fe85-7074-87a8-aac47f9c6aea",
    "019a3073-c44c-733e-ac5f-c50ac12982f8",
    "019a3076-0c00-7032-9482-a7ff155219da",
    "019a3078-3b93-73fe-bdb8-256969b9ebb9",
    "019a307a-aa92-7086-a4af-6d1db761749f",
    "019a307c-7736-7342-a631-a6364750137a",
    "019a4e59-b1c5-71a7-91bf-11342ba1411f",
    "019a4e5b-d730-719d-91a0-583b22351100",
    "019a4e5d-ae89-735b-bd3b-0faed42ad271",
    "019a4e5f-8481-70bb-bca2-21e8ab2d1caf",
    "019a4e64-7a36-70d2-bbc8-6463d137e8c6",
    "019a4e67-ad8c-71b1-ba2b-bba452f7f091",
    "019a4e6a-572c-715d-9858-a912d7a1f4ef",
    "019a6d62-3572-72e7-9ff0-374c0f6936d6",
    "019a6d5d-829d-7352-8a61-91412a8cda96",
    "019a6d66-ef86-72e2-868b-d8201eaff351",
  ],
  checkoutCateringDescription:
    "Din forespørgsel på dit valg af is til dit event, er nu klar til at blive sendt til os, når du har udfyldt nedenstående oplysninger og har gennemført betalingen.\n\nNår vi har modtaget din forespørgsel, hører du fra os indenfor 48 timer. Er din bestilling tilgængelig på din ønskede dato, modtager du en bekræftelse og din bestilling er nu registreret og betalt hos os.\nEr der fuldt booket på din ønskede dato, kontakter vi dig og aftaler nærmere med dig.\n\nNår vi har modtaget din forespørgsel, hører du fra os indenfor 48 timer. Er din bestilling tilgængelig på din ønskede dato, modtager du en bekræftelse og din bestilling er nu registreret og betalt hos os.\nEr der fuldt booket på din ønskede dato, kontakter vi dig og aftaler nærmere med dig. ",
  menuPageCateringDescription:
    "Uanset om det er en fødselsdag, et bryllup, en konfirmation eller et firma arrangement, kan vi stå for is til dit event.\nVi er af den overbevisning, at vores is kan gøre enhver fest endnu mere festlig.\nVores udvalg af isdiske varierer fra butik til butik, men herunder er en oversigt over forskellige isdiske og løsninger i lige præcis denne butik. Du har mulighed for at få leveret med eller uden bemanding, eller at afhente selv i vores butik. Se mere her om de forskellige løsninger vi har, som passer til netop dit event.\n\nHar du spørgsmål, kan du kontakte denne Paradis butik på kontaktoplysningerne herover.",
}

// MÆXICO
const maexico: ShopCateringSpec = {
  shopIds: [
    "01905e1b-b094-732b-8ae6-c4763dd7e00d",
    "0190119a-855d-71d8-94f5-5782268e1e4d",
    "01905e3f-faec-7190-a2af-f0ab372944ed",
  ],
  cateringOrderWindowStartInDays: 2,
  cateringOrderWindowEndInDays: moment()
    .add(2, "months")
    .diff(moment(), "days"),
  menuPageCateringDescription:
    "Der skal bestilles til minimum 5 personer. Vi forbeholder os retten til at afvise bestillingen, hvis der bestilles til mindre. Maden serveres i engangs-/aluminiumsbakker. Taco Kits er anrettet til buffet. Alt andet er ready-to-eat og vi anbefaler at maden spises senest 30 min. efter modtagelse. Hvis du ønsker de varme elementer pakket i en termokassen kan det tilføjes under engangsservice.",
  checkoutCateringDescription:
    "Emballage:\nJeres valgte menu kommer anrettet i engangsemballage. Ønsker I selv at anrette maden på jeres eget serveringsservice, er det let at overføre. I skal selv sørger for at have jeres eget service.\nDer kan bestilles servietter og engangsbestik.\n\nLevering:\nVi benytter Wolt som vores leveringsleverandør. Det valgte tidspunkt er altid et estimat, og vi kan ikke garantere præcis leveringstidspunkt. Leveringstidspunktet vil afhænge af trafik, vejr og kurértilgængelighed. For at sikre et præcist afhentningstidspunkt anbefaler vi, at du afhenter bestillingen selv.",
  checkoutInputs: [
    {
      name: "allergene",
      label: "Allergener og madhensyn",
      placeholder: "Indtast allergener eller madhensyn her",
      helperText:
        "Noter venligst eventuelle allergener og/eller særlige madhensyn her.",
    },
  ],
  cateringPaymentMethods: ["credit_card"],
}

// OLIOLI DHL
const olioliDHL: ShopCateringSpec = {
  shopIds: ["019b2ba2-2786-72c6-ba3f-4647f203e219"],
  cateringOrderWindowStartInDays: 1,
  cateringOrderWindowEndInDays: moment().add(1, "year").diff(moment(), "days"),
  menuPageCateringDescription:
    "Forudbestil dine bowls her og hav dem klar til afhentning i dagene fra Mandag - Fredag under DHL Stafetten 2026 i Fælledparken.\n\nVi tilbyder afhentning i tidsrummet 16.00-19.00 alle dage i DHL ugen.\n\nTil DHL tilbyder vi nedenstående varianter, og det er ikke muligt at udskifte/ændre ingredienserne.\n\nSåfremt I ikke bliver et antal der passer med vores pakker á 5 styk, er det muligt at købe enkelte bowls til DHL Stafetten i vores foodtruck. Den vil være placeret  samme sted som afhentningen af forudbestillinger. Der kan ikke forudbestilles fra vores foodtruck.\n\nFor oplysninger om allergener se venligst vores allergeneliste via følgende link: https://www.oliolipoke.dk/menu\n\nFor øvrige henvendelser kontakt os på info@oliolipoke.com.",
  checkoutCateringDescription:
    "Når du har aflagt din bestilling vil du kunne afhente maden på den dag du har valgt ved DHL OLIOLI teltet – placering kan du se på ovenstående billede – specifikt afhentningssted sendes på mail efter bestilling. Bemærk venligst, at der godt kan gå et par minutter før du modtager din købsbekræftelse og kvittering på mail.\n\nFor øvrige henvendelser bedes du kontakte os på info@oliolipoke.com. ",
  checkoutInputs: [
    {
      label: "Firmanavn",
      helperText: "Indtast dit firmanavn her",
      name: "company_name_catering",
      placeholder: "Indtast dit firmanavn her",
    },
  ],
  cateringPaymentMethods: ["credit_card"],
}

// Gorms - Delfino
const delfino: ShopCateringSpec = {
  shopIds: ["019c9484-7c98-71bd-821b-c59a49a03ad2"],
  cateringOrderWindowStartInDays: 2,
  cateringOrderWindowEndInDays: moment().add(6, "month").diff(moment(), "days"),
  menuPageCateringDescription:
    "Antal personer: Minimum 10 personer - Restauranten forbeholder sig retten til at afvise bestillingen, hvis der bestilles til mindre end 10 personer. Maden serveres i engang emballage. Imod gebyr kan vi anrette maden direkte på flotte serveringsfade. Kontakt restauranten direkte for dette.",
  checkoutCateringDescription:
    "Jeres valgte menu kommer anrettet til deling i engangsemballage. Ønsker I selv at anrette maden på jeres eget serveringsservice, er det let at overføre. I skal selv sørger for tag-tøj og bestik til maden. Nogle emner kan med fordel lunes, vejledning vil medfølge.",
  checkoutInputs: [],
  cateringPaymentMethods: ["credit_card"],
}

// Rørt
const roert: ShopCateringSpec = {
  shopIds: ["019cfad5-849f-71bd-8c9f-4c234043c27e"],
  cateringOrderWindowStartInDays: 3,
  cateringOrderWindowEndInDays: moment().add(6, "month").diff(moment(), "days"),
  menuPageCateringDescription:
    "Vi tager højde for allergener og særlige madhensyn. Noter eventuelle allergener i kommentarfeltet, når i bestiller. Minimumsordreværdi er 2500 kr. Smørrebrød leveres i særlige smørrebrødsæsker, og vi anbefaler at man bestiller til så tæt ved servering som muligt af hensyn til friskhed.",
  checkoutCateringDescription:
    "Vi tager højde for allergener og særlige madhensyn. Noter eventuelle allergener i kommentarfeltet, når i bestiller. Minimumsordreværdi er 2500 kr. Smørrebrød leveres i særlige smørrebrødsæsker, og vi anbefaler at man bestiller til så tæt ved servering som muligt af hensyn til friskhed.",
  checkoutInputs: [],
  cateringPaymentMethods: ["credit_card"],
}

// shopIds is an array of shop ids so that we can have same configuration for multiple shops.
export const SHOP_CATERING_SPEC: ShopCateringSpec[] = [
  humblePieThanksgiving,
  humblePieCatering,
  slagterBroeSmoerrebroed,
  slagterBroeSlagterbutikken,
  slagterBroeSlagterbutikkenTwo,
  madhuset,
  paradisIs,
  kcal,
  paradis,
  maexico,
  olioliDHL,
  madklubbenConfigOneCustomers,
  madklubbenConfigTwoCustomers,
  delfino,
  madklubbenLyngbyCatering,
  roert,
  {
    // OLIOLI
    shopIds: ["0198c6d6-150c-702a-be3d-1a5f99bed590"],
    cateringOrderWindowStartInDays: 3,
    cateringOrderWindowEndInDays: moment()
      .add(1, "year")
      .diff(moment(), "days"),
    menuPageCateringDescription:
      "Lad OLIOLI stå for maden til jeres næste arrangement. Vi serverer lækre, friske råvarer direkte på lokationen. Vores brede udvalg af Poké bowls sikrer, at der er noget for alle. Se mulighederne:\n\nDa vi er så heldige med stor efterspørgsel på caterings, så tager vi ikke imod henvendelser på under 30 kuverter.\n\nTil gengæld har vi en super smart hjemmeside og app, hvor du kan bestille klar til afhentning eller leveret direkte hjem til dig eller til dit kontor. Det er også muligt at bestille leje vores foodtruck til dit næste arrangement, kontakt os på event@oliolipoke.com for et uforpligtende tilbud.",
    checkoutCateringDescription:
      "Når vi har modtaget din forespørgsel vender vi tilbage hurtigst muligt og bekræfter din bestilling. Du kan altid kontakte os på event@oliolipoke.com eller info@oliolipoke.com. Vi giver dig med glæde et uforpligtende tilbud på catering, og hvad vi kan gøre for dig.",
    cateringPaymentMethods: ["credit_card"],
    checkoutInputs: [],
  },
  {
    shopIds: [
      // Catering Bone's
      "0195dc0a-c1f4-725d-a0e7-e53df08add6b",
      "0195ffa6-d8dd-739f-a45a-0ebcc36b6fc3",
      "0195fbba-2893-7171-b43f-aa577144cc75",
      "0195fbc1-99fd-73e8-bd72-0a95143bbd34",
      "0195dc0a-c1f4-725d-a0e7-e53df08add6b",
      "0195fbc6-067b-71f2-b224-51f95ab04308",
      "0195fb73-daa2-72f9-80fa-0e33489273c7",
      "0195fbcb-d892-70aa-aade-bd8950390977",
      "0195fb69-689a-7002-b82d-63672dfa1d81",
      "0195ffaa-724a-738d-a77f-288385ecbd8f",
      "0195fb46-6959-71fe-bb4e-a88f454e6197",
      "01961458-048c-722c-8642-36856048c8aa",
    ],
    menuPageCateringDescription:
      "Bestil amerikansk BBQ-catering hos Bone’s\n\nInvitér Bone’s med som gæst og gør din fest mindeværdig og velsmagende. Oplev den autentiske smag af Bone’s Real American BBQ som catering. Vores omhyggeligt udvalgte menu byder bl.a. på vores møre spareribs, smagfulde Tritip, velsmagende oksefilet, lækre friske salater, flødekartofler, ovnbagte kartofler, koldrøget laks, delikat cheesecake og meget mere.\n\nVi gør det let for dig - lige fra bestilling til levering eller afhentning. Du vælger nemlig helt selv, hvor stor en del af festen vi skal være en del af. Uanset sørger vores professionelle team for, at din fest fyldes af smagfulde øjeblikke, så du kan nyde American BBQ, helt uden besvær.",
    checkoutCateringDescription:
      "Med Bone’s på menuen!\n\nDu er i gang med at bestille catering fra Bone’s, og vi glæder os til at levere lækre retter, møre ribs og masser af hygge til din fest. Skulle du få brug for at ændre noget undervejs, så skriv endelig til os på catering@bones.dk – vi står klar til at hjælpe!",
    cateringOrderWindowStartInDays: 10,
    cateringOrderWindowEndInDays: moment()
      .add(4, "months")
      .diff(moment(), "days"),
    checkoutInputs: [],
    cateringPaymentMethods: ["ean"],
  },
  {
    shopIds: [
      // Rå sushi staging
      /* "950dfdd9-eb07-4263-a214-e32a78ba54cf", */
    ],
    menuPageCateringDescription:
      "Lad OLIOLI stå for maden til jeres næste arrangement. Vi serverer lækre, friske råvarer direkte på lokationen. Vores brede udvalg af Poké bowls sikrer, at der er noget for alle. Se mulighederne:\n\nDa vi er så heldige med stor efterspørgsel på caterings, så tager vi ikke imod henvendelser på under 30 kuverter.\n\nTil gengæld har vi en super smart hjemmeside og app, hvor du kan bestille klar til afhentning eller leveret direkte hjem til dig eller til dit kontor. Det er også muligt at bestille leje vores foodtruck til dit næste arrangement, kontakt os på event@oliolipoke.com for et uforpligtende tilbud.",
    checkoutCateringDescription:
      "Når vi har modtaget din forespørgsel vender vi tilbage hurtigst muligt og bekræfter din bestilling. Du kan altid kontakte os på event@oliolipoke.com eller info@oliolipoke.com. Vi giver dig med glæde et uforpligtende tilbud på catering, og hvad vi kan gøre for dig.",
    cateringOrderWindowStartInDays: 7,
    cateringOrderWindowEndInDays: moment()
      .add(1, "year")
      .diff(moment(), "days"),
    cateringPaymentMethods: ["credit_card"],
    checkoutInputs: [],
  },
]

// For using in nested components, so we dont have to find the spec for a single shop every time.
export const CateringProvider = ({ children, restaurant }: Props) => {
  const shopCateringSpec = getCateringSpec(restaurant?.shop.id)

  return (
    <CateringContext.Provider
      value={{
        checkoutCateringDescription:
          shopCateringSpec?.checkoutCateringDescription || "",
        isCatering: !!shopCateringSpec,
        cateringOrderWindowStartInDays:
          shopCateringSpec?.cateringOrderWindowStartInDays || 0,
        cateringOrderWindowEndInDays:
          shopCateringSpec?.cateringOrderWindowEndInDays || 0,
        menuPageCateringDescription:
          shopCateringSpec?.menuPageCateringDescription || "",
        checkoutInputs: shopCateringSpec?.checkoutInputs || [],
        cateringPaymentMethods: shopCateringSpec?.cateringPaymentMethods || [],
      }}
    >
      {children}
    </CateringContext.Provider>
  )
}
